import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi, type AdminUser } from '@/lib/api';
import { useMe } from '@/lib/me';
import { Badge, Button, Card, ErrorText, Input } from '@/components/ui';

export function AdminUsersPage() {
  const api = useApi();
  const [q, setQ] = useState('');
  const users = useQuery({ queryKey: ['admin', 'users', q], queryFn: () => api.admin.users(q) });
  return (
    <div className="space-y-4">
      <Input
        aria-label="Search users"
        placeholder="Search by email"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {users.isError && <ErrorText error={users.error} />}
      <Card>
        <ul className="divide-y divide-border">
          {users.data?.users.map((u) => (
            <UserRow key={u.id} u={u} />
          ))}
        </ul>
      </Card>
    </div>
  );
}

function UserRow({ u }: { u: AdminUser }) {
  const api = useApi();
  const me = useMe();
  const queryClient = useQueryClient();
  const [maxOrgs, setMaxOrgs] = useState(u.creatorGrant?.maxOrgs.toString() ?? '1');
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  const run = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onSuccess: refresh });
  const isSelf = me.data?.id === u.id;

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
      <div>
        <p className="font-medium">{u.email}</p>
        <p className="text-muted-foreground">{u.displayName ?? 'No name yet'}</p>
        <div className="mt-1 flex flex-wrap gap-2">
          {u.isPlatformAdmin && <Badge>Platform admin</Badge>}
          {u.suspendedAt && <Badge>Suspended</Badge>}
          {u.creatorGrant && (
            <Badge>
              Can create {u.creatorGrant.maxOrgs} org{u.creatorGrant.maxOrgs === 1 ? '' : 's'}
            </Badge>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label="Organizations they may create"
          type="number"
          min={1}
          className="w-20"
          value={maxOrgs}
          onChange={(e) => setMaxOrgs(e.target.value)}
        />
        <Button
          variant="secondary"
          disabled={run.isPending}
          onClick={() =>
            run.mutate(() => api.admin.setCreatorGrant(u.id, { maxOrgs: Number(maxOrgs) }))
          }
        >
          {u.creatorGrant ? 'Update quota' : 'Allow creating orgs'}
        </Button>
        {u.creatorGrant && (
          <Button
            variant="ghost"
            disabled={run.isPending}
            onClick={() => run.mutate(() => api.admin.revokeCreatorGrant(u.id))}
          >
            Revoke
          </Button>
        )}
        {!isSelf && (
          <>
            <Button
              variant="ghost"
              disabled={run.isPending}
              onClick={() => run.mutate(() => api.admin.setPlatformAdmin(u.id, !u.isPlatformAdmin))}
            >
              {u.isPlatformAdmin ? 'Remove admin' : 'Make admin'}
            </Button>
            <Button
              variant="ghost"
              disabled={run.isPending}
              onClick={() => {
                // Rare and audit-logged: learners' names lock after their one correction.
                const name = window.prompt(
                  `New name for ${u.email}. This also updates their existing certificates.`,
                  u.displayName ?? '',
                );
                if (name && name.trim() !== u.displayName)
                  run.mutate(() => api.admin.setDisplayName(u.id, name.trim()));
              }}
            >
              Change name
            </Button>
            <Button
              variant="ghost"
              disabled={run.isPending}
              onClick={() => run.mutate(() => api.admin.setSuspended(u.id, !u.suspendedAt))}
            >
              {u.suspendedAt ? 'Unsuspend' : 'Suspend'}
            </Button>
          </>
        )}
      </div>
      <div className="w-full">
        <ErrorText error={run.error} />
      </div>
    </li>
  );
}
